import React from "react";
import { ArrowUpRight, CreditCard, ShieldCheck } from "lucide-react";

const plans = [
    {
        id: "free-month",
        tag: "Free Month",
        sub: "30 days full access, then Launch Monthly unless cancelled.",
        computers: "1 computer total",
        recommended: true,
        options: [
            {
                code: "free_month",
                price: "£0",
                interval: "today",
                note: "Card required. Then £29/month after 30 days.",
            },
        ],
    },
    {
        id: "launch",
        tag: "Launch / Solo",
        sub: "For independent operators and starter teams.",
        computers: "Supports 1 computer total",
        options: [
            {
                code: "launch_annual",
                price: "£249",
                interval: "/year",
                note: "Best starting option",
            },
            {
                code: "launch_monthly",
                price: "£29",
                interval: "/month",
                note: "Monthly billing",
            },
        ],
    },
    {
        id: "professional",
        tag: "Professional",
        sub: "For agencies and teams prospecting every day.",
        computers: "Supports 3 computers total",
        options: [
            {
                code: "professional_annual",
                price: "£449",
                interval: "/year",
                note: "Best value professional option",
            },
            {
                code: "professional_monthly",
                price: "£49",
                interval: "/month",
                note: "Monthly billing",
            },
        ],
    },
    {
        id: "agency",
        tag: "Agency",
        sub: "For multiple users, devices, or agency workflows.",
        computers: "Supports 5 computers total",
        options: [
            {
                code: "agency_annual",
                price: "£749",
                interval: "/year",
                note: "Best value agency option",
            },
            {
                code: "agency_monthly",
                price: "£79",
                interval: "/month",
                note: "Monthly billing",
            },
        ],
    },
];

const details = [
    "Card details are handled securely by Stripe.",
    "Free Month is £0 today for 30 days, then converts to Launch Monthly.",
    "The licence key is created and emailed after checkout, including trial checkouts.",
    "Computer allowance depends on the selected plan.",
    "One licence key can be activated on the number of computers included in the selected plan.",
    "Renewals extend the same licence key.",
    "One Free Month is available per email or company.",
];

function selectedPlanFromUrl() {
    if (typeof window === "undefined") return "free_month";
    const plan = new URLSearchParams(window.location.search).get("plan");
    const validPlans = new Set(
        plans.flatMap((entry) => entry.options.map((option) => option.code)),
    );
    return validPlans.has(plan) ? plan : "free_month";
}

export default function PricingCheckout() {
    const selectedPlan = selectedPlanFromUrl();
    const query =
        typeof window !== "undefined"
            ? new URLSearchParams(window.location.search)
            : new URLSearchParams();
    const checkoutCancelled = query.get("checkout") === "cancelled";
    const missingDetails = query.get("error") === "missing_required_details";
    const freeMonthAlreadyUsed = query.get("error") === "free_month_already_used";

    return (
        <section
            id="pricing-checkout"
            data-testid="pricing-checkout-section"
            className="border-b border-border bg-ink text-bg"
        >
            <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                    <div className="lg:col-span-4">
                        <span
                            className="font-mono uppercase tracking-[0.2em] text-xs"
                            style={{ color: "hsl(var(--ink-mute))" }}
                        >
                            Pricing
                        </span>
                        <h1 className="font-display text-4xl md:text-5xl lg:text-7xl font-black tracking-tighter mt-4 leading-[0.95] text-bg">
                            Choose your ReachForge licence.
                        </h1>
                        <p className="text-bg/70 max-w-md mt-6 text-lg leading-relaxed">
                            Start Free Month or choose a paid plan. Stripe
                            collects card and billing details, and the licence
                            key is created automatically after checkout.
                        </p>

                        <div className="mt-10 space-y-px bg-bg/20 border border-bg/20">
                            {details.map((item) => (
                                <div
                                    key={item}
                                    className="bg-ink flex items-start gap-4 p-5"
                                >
                                    <div className="h-7 w-7 shrink-0 border border-bg/40 flex items-center justify-center text-forge">
                                        <ShieldCheck size={14} strokeWidth={3} />
                                    </div>
                                    <p className="text-bg/75 text-sm leading-relaxed m-0">
                                        {item}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {checkoutCancelled && (
                            <div className="mt-8 border border-forge bg-forge/10 p-5 text-bg">
                                <div className="font-display text-xl font-semibold">
                                    Checkout was cancelled.
                                </div>
                                <p className="text-bg/70 mt-2 text-sm">
                                    No payment was taken. You can choose a plan
                                    again when ready.
                                </p>
                            </div>
                        )}
                        {missingDetails && (
                            <div className="mt-8 border border-forge bg-forge/10 p-5 text-bg">
                                <div className="font-display text-xl font-semibold">
                                    Customer details are required.
                                </div>
                                <p className="text-bg/70 mt-2 text-sm">
                                    Enter full name, work email, and company
                                    name before continuing to Stripe checkout.
                                </p>
                            </div>
                        )}
                        {freeMonthAlreadyUsed && (
                            <div className="mt-8 border border-forge bg-forge/10 p-5 text-bg">
                                <div className="font-display text-xl font-semibold">
                                    Free Month already used.
                                </div>
                                <p className="text-bg/70 mt-2 text-sm">
                                    A Free Month has already been started for
                                    this email or company. Choose a paid plan or
                                    sign in to manage billing.
                                </p>
                            </div>
                        )}
                    </div>

                    <form
                        method="post"
                        action="/checkout"
                        data-testid="checkout-form"
                        className="lg:col-span-8 bg-bg text-ink border border-bg shadow-[12px_12px_0_0_hsl(var(--forge))] p-6 md:p-8 lg:p-10"
                    >
                        <div className="flex items-center gap-4 mb-8">
                            <div className="h-12 w-12 border border-ink flex items-center justify-center shrink-0">
                                <CreditCard size={19} strokeWidth={2.25} />
                            </div>
                            <div>
                                <span className="overline">Secure checkout</span>
                                <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-1 text-ink">
                                    Customer details
                                </h2>
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <label className="flex items-end justify-between mb-2">
                                    <span className="text-sm font-medium text-ink">
                                        Full name
                                    </span>
                                    <span className="font-mono text-[10px] tracking-[0.2em] text-ink-mute">
                                        01
                                    </span>
                                </label>
                                <input
                                    name="name"
                                    required
                                    type="text"
                                    maxLength="120"
                                    autoComplete="name"
                                    className="input-sharp"
                                    placeholder="Jane Smith"
                                />
                            </div>
                            <div>
                                <label className="flex items-end justify-between mb-2">
                                    <span className="text-sm font-medium text-ink">
                                        Work email
                                    </span>
                                    <span className="font-mono text-[10px] tracking-[0.2em] text-ink-mute">
                                        02
                                    </span>
                                </label>
                                <input
                                    name="email"
                                    required
                                    type="email"
                                    maxLength="160"
                                    autoComplete="email"
                                    className="input-sharp"
                                    placeholder="jane@company.com"
                                />
                            </div>
                            <div>
                                <label className="flex items-end justify-between mb-2">
                                    <span className="text-sm font-medium text-ink">
                                        Company name
                                    </span>
                                    <span className="font-mono text-[10px] tracking-[0.2em] text-ink-mute">
                                        03
                                    </span>
                                </label>
                                <input
                                    name="company_name"
                                    required
                                    type="text"
                                    maxLength="160"
                                    autoComplete="organization"
                                    className="input-sharp"
                                    placeholder="Acme Ltd."
                                />
                            </div>
                            <div>
                                <label className="flex items-end justify-between mb-2">
                                    <span className="text-sm font-medium text-ink">
                                        VAT number
                                    </span>
                                    <span className="font-mono text-[10px] tracking-[0.2em] text-ink-mute">
                                        Optional
                                    </span>
                                </label>
                                <input
                                    name="vat_number"
                                    type="text"
                                    maxLength="60"
                                    className="input-sharp"
                                    placeholder="GB123456789"
                                />
                            </div>
                        </div>

                        <div className="mt-9">
                            <span className="overline">Select plan</span>
                            <div className="grid lg:grid-cols-4 gap-px bg-border border border-border mt-4">
                                {plans.map((plan) => (
                                    <div
                                        key={plan.id}
                                        className="bg-bg p-5 md:p-6"
                                    >
                                        <div className="flex flex-col items-start gap-2 xl:flex-row xl:items-center xl:justify-between">
                                            <span className="font-mono uppercase tracking-[0.2em] text-xs text-ink-mute">
                                                {plan.tag}
                                            </span>
                                            {plan.recommended && (
                                                <span className="bg-forge text-bg px-2 py-1 font-mono text-[10px] tracking-[0.16em] uppercase whitespace-nowrap">
                                                    {plan.id === "free-month"
                                                        ? "30 days"
                                                        : "Best seller"}
                                                </span>
                                            )}
                                        </div>
                                        <p className="mt-3 text-sm text-ink-soft">
                                            {plan.sub}
                                        </p>
                                        <p className="mt-3 inline-flex border border-border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-ink-mute">
                                            {plan.computers}
                                        </p>
                                        <div className="mt-5 space-y-3">
                                            {plan.options.map((option) => (
                                                <label
                                                    key={option.code}
                                                    className="flex items-start gap-4 border border-border p-4 cursor-pointer hover:border-ink transition-colors"
                                                >
                                                    <input
                                                        type="radio"
                                                        name="plan"
                                                        value={option.code}
                                                        defaultChecked={
                                                            selectedPlan ===
                                                            option.code
                                                        }
                                                        required
                                                        className="mt-2"
                                                    />
                                                    <span className="block">
                                                        <span className="font-display text-3xl font-black tracking-tighter text-ink">
                                                            {option.price}
                                                        </span>
                                                        <span className="text-ink-soft ml-1">
                                                            {option.interval}
                                                        </span>
                                                        <span className="block overline mt-1">
                                                            {option.note}
                                                        </span>
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-mute max-w-md">
                                Stripe checkout · Free Month requires card
                                details · Renewal extends the same licence key
                            </p>
                            <button
                                type="submit"
                                data-testid="checkout-submit"
                                className="btn btn-accent"
                            >
                                Continue to secure checkout
                                <ArrowUpRight size={14} />
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}
